/**
 * The email status toast's model (YAR-477 / YAR-480): given the household's
 * recent emails, which state the ONE bottom toast is in and exactly what it
 * says. Pure — no React, no clock, no Supabase — so every row of the copy
 * table is a unit test, and phase 2 only has to render the result into the
 * shared toast slot (Figma `Toast / Status`, 10976:6373).
 *
 * Copy governs verbatim (Kelvin, Sep 23–24). Rules it encodes:
 * - "read", never "processed"; numbers always pair with a matching singular
 *   or plural noun; no exclamation points; no trailing periods.
 * - Sender line: the organisation resolved at ingestion, else the From
 *   display name (`emails.sender_label`), NEVER an address. No name → no
 *   line. Several: `A`, `A and B`, `A and [n] other senders`.
 * - A run is the emails that landed within 90 seconds of each other (the
 *   prototype's rule, YAR-480 open question). A failure still showing joins
 *   the next run instead of disappearing (AC10) — the caller passes those ids
 *   as `carriedIds`, and the same list carries a Try again's emails back in.
 * - "Couldn't be read" is the SERVER's verdict (status ERROR, including the
 *   5-minute reading timeout). The toast never times out on its own clock;
 *   the only local clock is the Still reading WORDING threshold below, which
 *   changes words and never declares a failure (AC4).
 *
 * Decisions the copy table leaves open, made here and pinned in tests:
 * - Mixed's `[N]` is every email in the run (Figma: "9 new items / Across 3
 *   emails · 1 couldn't be read").
 * - Failures with successes that found NOTHING show Couldn't be read (only
 *   the failures are actionable; the table has no "nothing + failed" row).
 * - Plan limit applies only when nothing failed; with failures the toast is
 *   Mixed, whose Try again the parent still needs.
 * - Try again gives way (See email / See emails) only when EVERY failed
 *   email in the run has failed at least twice.
 */

import { isReadingStatus } from '@/lib/emailReadState';

/** Emails landing within this many ms of the previous one join its run. */
export const RUN_WINDOW_MS = 90_000;

/** Found and Nothing found leave after 6 s of VISIBLE time (AC5). */
export const RESULT_AUTO_DISMISS_MS = 6_000;

/**
 * When Reading turns into "Still reading … Taking longer than usual". Only
 * the wording changes; the give-up is the server's 5-minute timeout. Best
 * case extraction is 5–10 s (Meeting #146), so a minute is well past normal.
 */
export const STILL_READING_AFTER_MS = 60_000;

/** A second failure swaps Try again for See email(s) (AC9). */
export const FAILURES_BEFORE_SEE_EMAIL = 2;

export interface RunEmail {
  id: string;
  /** `emails.status`. */
  status: string;
  /** `emails.failure_count`. */
  failureCount: number;
  /** `emails.sender_label` — never an address (enforced again below). */
  senderLabel: string | null;
  /** ISO arrival time (`emails.created_at`). */
  createdAt: string;
  /**
   * ISO start of the current read (`processing_started_at`, else the time it
   * was queued). Only used for the Still reading wording.
   */
  readingStartedAt?: string | null;
  /** Review items found in this email (not dismissed). */
  itemCount: number;
}

export interface RunInput {
  emails: readonly RunEmail[];
  /** Epoch ms "now" — injected so the model stays pure. */
  now: number;
  /** The household is at its plan limit (confirming is blocked; AC11). */
  quotaReached?: boolean;
  /**
   * Ids still showing from the previous toast (unacknowledged failures) or
   * just sent through Try again. They join the current run.
   */
  carriedIds?: readonly string[];
}

export type RunState =
  | 'reading-one'
  | 'reading-several'
  | 'still-reading'
  | 'found'
  | 'nothing-found'
  | 'mixed'
  | 'couldnt-read'
  | 'couldnt-read-twice'
  | 'plan-limit';

export type RunAction =
  | 'review'
  | 'try-again'
  | 'see-email'
  | 'see-emails'
  | 'upgrade-plan';

export const ACTION_LABELS: Record<RunAction, string> = {
  review: 'Review',
  'try-again': 'Try again',
  'see-email': 'See email',
  'see-emails': 'See emails',
  'upgrade-plan': 'Upgrade plan',
};

/** Figma status icon per state: glyph + tone ("icon colour carries the state"). */
export type RunIcon =
  | 'sparkles'
  | 'sparkles-warning'
  | 'circle-check'
  | 'triangle-alert'
  | 'circle-alert';

export interface LineSegment {
  text: string;
  tone?: 'error';
}

export interface StatusToastModel {
  state: RunState;
  headline: string;
  /** Plain second line, or null when there is none (e.g. no sender name). */
  secondLine: string | null;
  /** The same line as styled segments (Mixed paints its failure count red). */
  secondLineSegments: LineSegment[];
  actions: RunAction[];
  icon: RunIcon;
  /** ms of visible time before it leaves, or null when it stays until acted on. */
  autoDismissMs: number | null;
  /** Every email in the run. */
  emailIds: string[];
  /** The run's couldn't-read emails — what Try again re-reads. */
  failedIds: string[];
}

// ── helpers ────────────────────────────────────────────────────────────────

/** `1 email`, `2 emails`. */
export function countNoun(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function cleanLabel(label: string | null | undefined): string | null {
  const v = label?.trim();
  if (!v || v.includes('@')) return null;
  return v;
}

/**
 * The sender rule: `A`, `A and B`, `A and [n] other senders`. Distinct named
 * senders in run order; unnamed emails are not counted (there is nothing to
 * name). Null when no email has a name.
 */
export function senderLine(labels: readonly (string | null)[]): string | null {
  const names: string[] = [];
  for (const raw of labels) {
    const name = cleanLabel(raw);
    if (name && !names.includes(name)) names.push(name);
  }
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} and ${names.length - 1} other senders`;
}

function time(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * The current run: the cluster of emails, chained by arrival within
 * RUN_WINDOW_MS, that contains the most recent arrival — plus every carried
 * id. Returned oldest first.
 */
export function selectRun(
  emails: readonly RunEmail[],
  carriedIds: readonly string[] = [],
): RunEmail[] {
  if (emails.length === 0) return [];
  const byArrival = [...emails].sort(
    (a, b) => time(a.createdAt) - time(b.createdAt),
  );
  const run: RunEmail[] = [byArrival[byArrival.length - 1]];
  for (let i = byArrival.length - 2; i >= 0; i--) {
    const gap = time(run[0].createdAt) - time(byArrival[i].createdAt);
    if (gap > RUN_WINDOW_MS) break;
    run.unshift(byArrival[i]);
  }
  const inRun = new Set(run.map((e) => e.id));
  const carried = byArrival.filter(
    (e) => carriedIds.includes(e.id) && !inRun.has(e.id),
  );
  return [...carried, ...run];
}

function isStalled(email: RunEmail, now: number): boolean {
  if (email.status === 'HELD') return true;
  const started = time(email.readingStartedAt ?? email.createdAt);
  return started > 0 && now - started >= STILL_READING_AFTER_MS;
}

function plain(line: string | null): LineSegment[] {
  return line === null ? [] : [{ text: line }];
}

// ── the model ──────────────────────────────────────────────────────────────

export function computeStatusToast(input: RunInput): StatusToastModel | null {
  const run = selectRun(input.emails, input.carriedIds);
  if (run.length === 0) return null;

  const total = run.length;
  const emailIds = run.map((e) => e.id);
  const reading = run.filter((e) => isReadingStatus(e.status));
  const failed = run.filter((e) => e.status === 'ERROR');
  const failedIds = failed.map((e) => e.id);
  const succeeded = run.filter(
    (e) => !isReadingStatus(e.status) && e.status !== 'ERROR',
  );
  const found = succeeded.reduce((sum, e) => sum + Math.max(0, e.itemCount), 0);
  const foundHeadline = `Yaruta found ${countNoun(found, 'new item', 'new items')}`;
  const base = { emailIds, failedIds };

  // ── Still reading ────────────────────────────────────────────────────────
  if (reading.length > 0) {
    if (reading.some((e) => isStalled(e, input.now))) {
      const line = 'Taking longer than usual';
      return {
        ...base,
        state: 'still-reading',
        headline: `Still reading ${countNoun(reading.length, 'email', 'emails')}`,
        secondLine: line,
        secondLineSegments: plain(line),
        actions: [],
        icon: 'sparkles-warning',
        autoDismissMs: null,
      };
    }
    if (total === 1) {
      const line = senderLine([run[0].senderLabel]);
      return {
        ...base,
        state: 'reading-one',
        headline: 'Reading new email',
        secondLine: line,
        secondLineSegments: plain(line),
        actions: [],
        icon: 'sparkles',
        autoDismissMs: null,
      };
    }
    const line = `${total - reading.length} of ${total} read`;
    return {
      ...base,
      state: 'reading-several',
      headline: `Reading ${countNoun(total, 'email', 'emails')}`,
      secondLine: line,
      secondLineSegments: plain(line),
      actions: [],
      icon: 'sparkles',
      autoDismissMs: null,
    };
  }

  // ── Done: failures ───────────────────────────────────────────────────────
  if (failed.length > 0) {
    const allTwice = failed.every(
      (e) => e.failureCount >= FAILURES_BEFORE_SEE_EMAIL,
    );

    if (succeeded.length > 0 && found > 0) {
      const failPart = `${failed.length} couldn't be read`;
      const lead = `Across ${countNoun(total, 'email', 'emails')}`;
      return {
        ...base,
        state: 'mixed',
        headline: foundHeadline,
        secondLine: `${lead} · ${failPart}`,
        secondLineSegments: [
          { text: `${lead} · ` },
          { text: failPart, tone: 'error' },
        ],
        // Review already goes where See email(s) would, so it is not added.
        actions: allTwice ? ['review'] : ['review', 'try-again'],
        icon: 'triangle-alert',
        autoDismissMs: null,
      };
    }

    const line = senderLine(failed.map((e) => e.senderLabel));
    return {
      ...base,
      state: allTwice ? 'couldnt-read-twice' : 'couldnt-read',
      headline: `${countNoun(failed.length, 'email', 'emails')} couldn't be read`,
      secondLine: line,
      secondLineSegments: plain(line),
      actions: allTwice
        ? [failed.length === 1 ? 'see-email' : 'see-emails']
        : ['try-again'],
      icon: 'circle-alert',
      autoDismissMs: null,
    };
  }

  // ── Done: no failures ────────────────────────────────────────────────────
  if (found > 0 && input.quotaReached) {
    const line = 'Limit reached, upgrade to continue';
    return {
      ...base,
      state: 'plan-limit',
      headline: foundHeadline,
      secondLine: line,
      secondLineSegments: plain(line),
      actions: ['upgrade-plan'],
      icon: 'triangle-alert',
      autoDismissMs: null,
    };
  }

  if (found > 0) {
    const sender = total === 1 ? senderLine([run[0].senderLabel]) : null;
    const line =
      total === 1
        ? sender === null
          ? null
          : `From ${sender}`
        : `Across ${countNoun(total, 'email', 'emails')}`;
    return {
      ...base,
      state: 'found',
      headline: foundHeadline,
      secondLine: line,
      secondLineSegments: plain(line),
      actions: ['review'],
      icon: 'circle-check',
      autoDismissMs: RESULT_AUTO_DISMISS_MS,
    };
  }

  const line = 'Nothing to add';
  return {
    ...base,
    state: 'nothing-found',
    headline: `Yaruta read ${countNoun(total, 'email', 'emails')}`,
    secondLine: line,
    secondLineSegments: plain(line),
    actions: ['review'],
    icon: 'sparkles',
    autoDismissMs: RESULT_AUTO_DISMISS_MS,
  };
}
